import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import '../styles/dropdown.css';

export interface DropdownOption {
  value: string;
  label: string;
  /** graue Zusatzzeile unter dem Eintrag */
  sub?: string | null;
  /** Gruppenüberschrift; aufeinanderfolgende Einträge mit gleicher Gruppe stehen zusammen */
  group?: string;
  disabled?: boolean;
}

/** Ab so vielen Einträgen bekommt die Liste ein Suchfeld */
const SEARCH_FROM = 9;

const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ß/g, 'ss');

/** Liste unter (oder über) dem Auslöser, als Portal, damit Tabellen, Modals und Abschnitte sie nicht abschneiden */
function usePopover(anchor: RefObject<HTMLElement | null>, open: boolean, minWidth?: number) {
  const [style, setStyle] = useState<CSSProperties>({ visibility: 'hidden' });
  const place = useCallback(() => {
    const el = anchor.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const below = vh - r.bottom - 8;
    const above = r.top - 8;
    const up = below < 220 && above > below;
    const maxH = Math.max(140, Math.min(340, up ? above : below));
    const width = Math.min(Math.max(r.width, minWidth ?? 0), vw - 16);
    const left = Math.max(8, Math.min(r.left, vw - width - 8));
    setStyle(
      up
        ? { left, width, bottom: vh - r.top + 4, maxHeight: maxH }
        : { left, width, top: r.bottom + 4, maxHeight: maxH },
    );
  }, [anchor, minWidth]);
  useLayoutEffect(() => {
    if (!open) return;
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);
  return style;
}

function useOutside(refs: RefObject<HTMLElement | null>[], open: boolean, close: () => void) {
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (!refs.some((r) => r.current?.contains(e.target as Node))) close();
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, close, refs]);
}

function OptionRows({
  items,
  active,
  value,
  onPick,
  onHover,
  listRef,
}: {
  items: DropdownOption[];
  active: number;
  value?: string;
  onPick: (o: DropdownOption) => void;
  onHover: (i: number) => void;
  listRef: RefObject<HTMLDivElement | null>;
}) {
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-i="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, listRef]);
  let lastGroup: string | undefined;
  return (
    <div className="dd-list" ref={listRef} role="listbox">
      {items.map((o, i) => {
        const head = o.group && o.group !== lastGroup ? o.group : null;
        lastGroup = o.group;
        const on = value !== undefined && o.value === value;
        return (
          <div key={`${o.value}-${i}`}>
            {head && <div className="dd-group">{head}</div>}
            <div
              data-i={i}
              role="option"
              aria-selected={on}
              aria-disabled={o.disabled || undefined}
              className={`dd-opt${i === active ? ' active' : ''}${on ? ' on' : ''}${o.disabled ? ' off' : ''}${o.value === '' ? ' empty' : ''}`}
              onMouseEnter={() => onHover(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => !o.disabled && onPick(o)}
            >
              <span className="dd-label">
                {o.label}
                {o.sub && <span className="dd-sub">{o.sub}</span>}
              </span>
              {on && <Icon name="check" className="dd-check" />}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Dropdown: Auswahl aus festen Werten, ersetzt das native <select>.
 * Leerer Wert über `placeholder` (erscheint als erster Eintrag, solange `clearable` nicht false ist).
 */
export function Dropdown({
  value,
  onChange,
  options,
  placeholder = '–',
  clearable = true,
  disabled,
  id,
  size,
  className = '',
  title,
  minListWidth,
  searchable,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
}: {
  value: string | number | null | undefined;
  onChange: (v: string) => void;
  options: DropdownOption[];
  placeholder?: string;
  clearable?: boolean;
  disabled?: boolean;
  id?: string;
  size?: 'sm';
  className?: string;
  title?: string;
  minListWidth?: number;
  searchable?: boolean;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const typed = useRef({ s: '', t: 0 });
  const v = value == null ? '' : String(value);
  const all = useMemo(
    () => (clearable ? [{ value: '', label: placeholder }, ...options] : options),
    [clearable, placeholder, options],
  );
  const withSearch = searchable ?? options.length >= SEARCH_FROM;
  const items = useMemo(() => {
    if (!q.trim()) return all;
    const n = norm(q.trim());
    return all.filter(
      (o) => o.value !== '' && (norm(o.label).includes(n) || (o.sub && norm(o.sub).includes(n))),
    );
  }, [all, q]);
  const current = options.find((o) => o.value === v);
  const style = usePopover(btn, open, minListWidth);
  const close = useCallback(() => setOpen(false), []);
  const refs = useMemo(() => [btn, pop], []);
  useOutside(refs, open, close);

  const show = () => {
    if (disabled) return;
    setQ('');
    setActive(
      Math.max(
        0,
        all.findIndex((o) => o.value === v),
      ),
    );
    setOpen(true);
  };
  useEffect(() => {
    if (open && withSearch) search.current?.focus();
  }, [open, withSearch]);
  const pick = (o: DropdownOption) => {
    setOpen(false);
    btn.current?.focus();
    if (o.value !== v) onChange(o.value);
  };
  const move = (d: number) => {
    if (!items.length) return;
    let i = active;
    for (let k = 0; k < items.length; k++) {
      i = (i + d + items.length) % items.length;
      if (!items[i]!.disabled) break;
    }
    setActive(i);
  };
  const onKey = (e: KeyboardEvent) => {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        show();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      move(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      move(-1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActive(items.length - 1);
    } else if (e.key === 'Enter' || (e.key === ' ' && !withSearch)) {
      e.preventDefault();
      const o = items[active];
      if (o && !o.disabled) pick(o);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      btn.current?.focus();
    } else if (e.key === 'Tab') setOpen(false);
    else if (!withSearch && e.key.length === 1) {
      const now = Date.now();
      typed.current = { s: now - typed.current.t < 700 ? typed.current.s + e.key : e.key, t: now };
      const n = norm(typed.current.s);
      const i = items.findIndex((o) => o.value !== '' && norm(o.label).startsWith(n));
      if (i >= 0) setActive(i);
    }
  };

  return (
    <>
      <button
        type="button"
        id={id}
        ref={btn}
        className={`dd-trigger${size === 'sm' ? ' sm' : ''}${open ? ' open' : ''}${current ? '' : ' placeholder'} ${className}`}
        disabled={disabled}
        title={title ?? (current?.label && current.label.length > 24 ? current.label : undefined)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid || undefined}
        onClick={(e) => {
          e.stopPropagation();
          if (open) setOpen(false);
          else show();
        }}
        onKeyDown={onKey}
      >
        <span className="dd-value">{current?.label ?? (v ? v : placeholder)}</span>
        <Icon name="chevDown" className="dd-chev" />
      </button>
      {open &&
        createPortal(
          <div
            className="dd-pop"
            ref={pop}
            style={style}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={onKey}
          >
            {withSearch && (
              <div className="dd-search">
                <Icon name="search" />
                <input
                  ref={search}
                  value={q}
                  placeholder="Suchen …"
                  aria-label="Liste durchsuchen"
                  onChange={(e) => {
                    setQ(e.target.value);
                    setActive(0);
                  }}
                />
              </div>
            )}
            {items.length ? (
              <OptionRows
                items={items}
                active={active}
                value={v}
                onPick={pick}
                onHover={setActive}
                listRef={listRef}
              />
            ) : (
              <div className="dd-none">Nichts gefunden</div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * ComboInput: Freitext mit Vorschlägen aus einer Liste (z. B. Region). Ersetzt <input list> mit <datalist>,
 * das der Browser mit eigenem Verlauf vermischt.
 */
export function ComboInput({
  value,
  onChange,
  options,
  id,
  placeholder,
  disabled,
  newHint,
  emptyHint,
}: {
  value: string;
  onChange: (v: string) => void;
  options: DropdownOption[];
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Text für einen Wert, der noch nicht in der Liste steht, z. B. „wird als neue Region gespeichert“ */
  newHint?: string;
  emptyHint?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [filter, setFilter] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const items = useMemo(() => {
    const n = norm(value.trim());
    if (!filter || !n) return options;
    return options.filter((o) => norm(o.label).includes(n));
  }, [options, value, filter]);
  const known = options.some((o) => norm(o.label) === norm(value.trim()));
  const style = usePopover(wrap, open);
  const close = useCallback(() => setOpen(false), []);
  const refs = useMemo(() => [wrap, pop], []);
  useOutside(refs, open, close);
  const pick = (o: DropdownOption) => {
    onChange(o.label);
    setOpen(false);
    input.current?.focus();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) return (setOpen(true), setActive(0));
      setActive((a) => Math.min(items.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Enter' && open && items[active]) {
      e.preventDefault();
      pick(items[active]!);
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    } else if (e.key === 'Tab') setOpen(false);
  };
  return (
    <div className={`dd-combo${open ? ' open' : ''}`} ref={wrap}>
      <input
        id={id}
        ref={input}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        onFocus={() => (setFilter(false), setOpen(true), setActive(-1))}
        onClick={() => !open && (setFilter(false), setOpen(true))}
        onChange={(e) => {
          onChange(e.target.value);
          setFilter(true);
          setOpen(true);
          setActive(0);
        }}
        onKeyDown={onKey}
      />
      <button
        type="button"
        tabIndex={-1}
        className="dd-combo-btn"
        aria-label="Vorschläge zeigen"
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => (open ? setOpen(false) : (setFilter(false), setOpen(true), input.current?.focus()))}
      >
        <Icon name="chevDown" className="dd-chev" />
      </button>
      {open &&
        createPortal(
          <div className="dd-pop" ref={pop} style={style}>
            {items.length > 0 && (
              <OptionRows
                items={items}
                active={active}
                value={options.find((o) => norm(o.label) === norm(value.trim()))?.value ?? undefined}
                onPick={pick}
                onHover={setActive}
                listRef={listRef}
              />
            )}
            {!items.length && !value.trim() && (
              <div className="dd-none">{emptyHint ?? 'Keine Vorschläge'}</div>
            )}
            {value.trim() && !known && newHint && (
              <div className="dd-new">
                <Icon name="plus" /> „{value.trim()}“ {newHint}
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
